import { Link } from "react-router-dom";

const linkCls = "text-[var(--lt-terracotta)] font-semibold hover:underline";

const STEPS = [
    ["Lessons", "/lessons", "Twelve structured lessons, six for English and six for Traditional Nepali, start on the home row and build up to full words and sentences."],
    ["Practice", "/practice", "Type real sentences at your own pace. The on-screen keyboard highlights the next key, and live counters show your speed and accuracy."],
    ["Timed tests", "/test", "Choose a 30, 60 or 120 second test in English or Nepali to measure your words per minute and accuracy."],
    ["Leaderboard", "/leaderboard", "Log in and your test results count towards separate English and Nepali leaderboards."],
];

const TIPS = [
    "Keep your fingers anchored on the home row and return to it after every reach.",
    "Look at the screen, not your hands. Use the on-screen keyboard if you forget a key.",
    "Slow down until you can type a line with almost no mistakes, then speed up gradually.",
    "Practise a little every day. Short, regular sessions beat one long session a week.",
    "Sit upright with relaxed shoulders and let your wrists float instead of resting on the desk.",
    "Notice which keys you miss most and go back to the lesson that covers them.",
];

const FAQ = [
    ["Is LipiTyping free?", "Yes. Lessons, practice, timed tests, the leaderboard and the utilities are free to use."],
    ["Do I need an account?", "No. You can use lessons, practice and tests without signing up. A free account is only needed if you want your results saved and shown on the leaderboard."],
    ["Which Nepali keyboard layout is used?", "The Traditional Nepali (Devanagari) layout. The app maps your physical keys to Devanagari in the browser, so you do not need to install or switch a system keyboard."],
    ["How is WPM calculated?", "LipiTyping counts only correctly typed characters, divides them by five to get words, and divides that by the minutes elapsed. Accuracy is correct characters as a share of everything you typed."],
    ["Does it work on mobile?", "You can read the blog and use the utilities on a phone. The typing exercises read physical key presses, so they are designed for a computer or a device with a hardware keyboard."],
    ["Where is my progress saved?", "When you are logged in, each completed practice or test result is saved to your LipiTyping account and shown on your dashboard. Without an account, results are not saved."],
];

function Section({ title, children, testid }) {
    return (
        <section className="max-w-4xl mx-auto px-6 py-10" data-testid={testid}>
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-4">{title}</h2>
            <div className="space-y-4 text-[var(--lt-muted)] leading-relaxed">{children}</div>
        </section>
    );
}

export default function HomeContent() {
    return (
        <div className="pb-10">
            <Section title="How LipiTyping works" testid="home-how-it-works">
                <div className="grid sm:grid-cols-2 gap-4">
                    {STEPS.map(([t, to, d]) => (
                        <div key={t} className="glass rounded-xl p-5">
                            <Link to={to} className={`${linkCls} text-lg`}>{t}</Link>
                            <p className="mt-2 text-sm">{d}</p>
                        </div>
                    ))}
                </div>
            </Section>

            <Section title="WPM and accuracy, explained" testid="home-wpm-accuracy">
                <p>WPM means words per minute. A "word" is counted as five characters, so typing 250 correct characters in one minute is 50 WPM. In LipiTyping only correct characters count towards your speed.</p>
                <p>Accuracy is the share of characters you typed correctly. It matters first because every mistake costs time twice: once to make it and once to fix it. Habits you repeat while rushing also become the habits your fingers keep. Aim for clean, steady typing, and speed will follow.</p>
            </Section>

            <Section title="Typing in Traditional Nepali (Devanagari)" testid="home-nepali-intro">
                <p>The Traditional layout gives each Devanagari letter a fixed key, so the same key always produces the same letter. On the home row, the left hand rests on <span className="font-nepali text-[var(--lt-text)]">ब क म ा</span> (A S D F) and the right hand on <span className="font-nepali text-[var(--lt-text)]">व प ि स</span> (J K L ;), with <span className="font-nepali text-[var(--lt-text)]">न</span> and <span className="font-nepali text-[var(--lt-text)]">ज</span> on G and H.</p>
                <p>Holding Shift on most consonant keys types the half letter, such as <span className="font-nepali text-[var(--lt-text)]">क्</span> or <span className="font-nepali text-[var(--lt-text)]">स्</span>, and the backslash key types the halanta sign. The number row gives Devanagari digits and the full stop key types the danda (<span className="font-nepali text-[var(--lt-text)]">।</span>). Start with the <Link to="/lessons" className={linkCls}>Nepali lessons</Link> to learn it step by step.</p>
            </Section>

            <Section title="Practical tips for speed and accuracy" testid="home-tips">
                <ul className="list-disc pl-5 space-y-2">
                    {TIPS.map((t) => <li key={t}>{t}</li>)}
                </ul>
                <p>Try them in <Link to="/practice" className={linkCls}>free practice</Link>, then check your progress with a <Link to="/test" className={linkCls}>timed test</Link>. For more guides, visit the <Link to="/blog" className={linkCls}>blog</Link>, or explore the <Link to="/utilities" className={linkCls}>Nepal utilities</Link>.</p>
            </Section>

            <Section title="Frequently asked questions" testid="home-faq">
                <div className="space-y-3">
                    {FAQ.map(([q, a], i) => (
                        <div key={q} className="glass rounded-xl p-5" data-testid={`home-faq-item-${i}`}>
                            <h3 className="text-[var(--lt-text)] font-semibold">{q}</h3>
                            <p className="mt-2 text-sm">{a}</p>
                        </div>
                    ))}
                </div>
            </Section>
        </div>
    );
}
